/**
 * One number with its label, and a link to where it comes from: "12 Users".
 *
 * `value` undefined is "still loading" and shows a skeleton. The page decides
 * whether the tile appears at all — a tile for a number the caller cannot
 * read would never fill in.
 */
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { SvgIconComponent } from '@mui/icons-material';

export interface StatTileProps {
  icon: SvgIconComponent;
  label: string;
  value: number | undefined;
  /** Where a click goes. */
  to: string;
  hint?: string;
}

export function StatTile({ icon: Icon, label, value, to, hint }: StatTileProps) {
  return (
    <Card variant="outlined">
      <CardActionArea component={RouterLink} to={to} sx={{ p: 2.5, height: '100%' }}>
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'action.selected',
              color: 'primary.main',
            }}
          >
            <Icon />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {value === undefined ? <Skeleton width={40} /> : value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {label}
            </Typography>
            {hint && (
              <Typography variant="caption" color="text.secondary">
                {hint}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardActionArea>
    </Card>
  );
}
